import { useRef, useState } from 'react';
import { faGrid2, faPlus } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import { useShallow } from 'zustand/react/shallow';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Icon } from '@/components/ui/icon';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useCreateBlankPage } from '@/hooks/use-create-blank-page';
import { usePagesStore } from '@/stores/pages-store';
import { PageType } from '@/types/page';
import { AddBlogModal } from './add-blog-modal';
import { AddCaseResultModal } from './add-case-result-modal';
import { AddPageModal } from './add-page-modal';
import { INDEX_PAGE_TYPES, type IndexPageType } from './add-page-modal/page-template-types';
import { AddReviewModal } from './add-review-modal';

type AddPageDialog = 'pageTemplates' | IndexPageType['key'] | 'review' | 'case_result' | 'blog';

const CONTENT_DIALOGS = {
  [PageType.REVIEW_INDEX]: 'review',
  [PageType.CASE_RESULT_INDEX]: 'case_result',
  [PageType.BLOG_INDEX]: 'blog',
} as const;

export function AddPageMenu() {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const pendingDialog = useRef<AddPageDialog | null>(null);
  const [dialog, setDialog] = useState<AddPageDialog | null>(null);
  const { createBlankPage, isCreating } = useCreateBlankPage();
  const hasLoaded = usePagesStore((state) => state.hasLoaded);
  const existingIndexPages = usePagesStore(
    useShallow((state) => INDEX_PAGE_TYPES.map(({ key }) => state.hasIndexPageType(key))),
  );

  const handleOpenChange = (open: boolean) => {
    if (!open) setDialog(null);
  };
  const restoreFocus = (event: Event) => {
    event.preventDefault();
    triggerRef.current?.focus();
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={isCreating}>
          <Button ref={triggerRef} size="sm" isLoading={isCreating} loadingText="Creating...">
            <Icon icon={faPlus} /> Add Page
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="w-50 rounded-lg p-2"
          onCloseAutoFocus={(event) => {
            if (!pendingDialog.current) return;
            // Open after the menu releases focus so the dialog keeps its focus trap.
            event.preventDefault();
            setDialog(pendingDialog.current);
            pendingDialog.current = null;
          }}
        >
          <DropdownMenuItem onSelect={() => void createBlankPage()}>
            <Icon icon={faPlus} /> Blank Page
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => {
              pendingDialog.current = 'pageTemplates';
            }}
          >
            <Icon icon={faGrid2} /> Use Template
          </DropdownMenuItem>
          {INDEX_PAGE_TYPES.map(({ key, label, icon }, index) => {
            const alreadyExists = existingIndexPages[index];
            const disabledReason = !hasLoaded ? 'Loading pages...' : undefined;

            const item = (
              <DropdownMenuItem
                key={key}
                disabled={!!disabledReason}
                aria-description={disabledReason}
                onSelect={() => {
                  pendingDialog.current = alreadyExists ? CONTENT_DIALOGS[key] : key;
                }}
              >
                <Icon icon={icon} /> {label}
              </DropdownMenuItem>
            );

            if (!disabledReason) return item;

            return (
              <Tooltip key={key}>
                <TooltipTrigger asChild>
                  <div>{item}</div>
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={8} className="max-w-xs">
                  {disabledReason}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
      {dialog === 'review' ? (
        <AddReviewModal onOpenChange={handleOpenChange} onCloseAutoFocus={restoreFocus} />
      ) : dialog === 'blog' ? (
        <AddBlogModal open onOpenChange={handleOpenChange} onCloseAutoFocus={restoreFocus} />
      ) : dialog === 'case_result' ? (
        <AddCaseResultModal open onOpenChange={handleOpenChange} onCloseAutoFocus={restoreFocus} />
      ) : dialog ? (
        <AddPageModal
          open
          onOpenChange={handleOpenChange}
          childPageType={dialog === 'pageTemplates' ? undefined : dialog}
          onCloseAutoFocus={restoreFocus}
        />
      ) : null}
    </>
  );
}
